// @ts-nocheck
import React, { useEffect, useRef, useState, useCallback } from "react";
import { API_URL } from "@/env";
import VideoLoader from "@/assets/icons/VideoLoader.svg";
import Resize from "@/assets/icons/Resize.svg";

const DOMReplayViewer = ({ reportId, containerWidth }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [events, setEvents] = useState([]);
  const [reportInfo, setReportInfo] = useState(null);
  const [playerReady, setPlayerReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Timeline state
  const [currentTime, setCurrentTime] = useState(0);
  const [totalDuration, setTotalDuration] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const playerContainerRef = useRef(null);
  const playerInstanceRef = useRef(null);
  const timelineRef = useRef(null);
  const timelineIntervalRef = useRef(null);
  const dragStartTimeRef = useRef(null);
  const isPlayingRef = useRef(false);
  const currentTimeRef = useRef(0);
  const wrapperRef = useRef(null);
  const fullscreenContainerRef = useRef(null);

  useEffect(() => {
    fetchRecording();
  }, [reportId]);

  const fetchRecording = async () => {
    try {
      setLoading(true);
      setError(null);
      setPlayerReady(false);

      const reportResponse = await fetch(`${API_URL}/reports/${reportId}`);
      if (!reportResponse.ok) {
        throw new Error(`Failed to fetch report: ${reportResponse.statusText}`);
      }

      const reportData = await reportResponse.json();
      setReportInfo(reportData.info);

      if (reportData.recordingChunks && reportData.recordingChunks.length > 0) {
        const sortedChunks = reportData.recordingChunks.sort(
          (a, b) => a.index - b.index
        );
        const allEvents = sortedChunks.flatMap((chunk) => chunk.events || []);

        // console.log("Raw events sample:", allEvents.slice(0, 5));

        const processedEvents = allEvents.map((event) => {
          if (event.type === 2) {
            return processFullSnapshotEvent(event);
          }
          return event;
        });

        // Sort events by timestamp - CRITICAL for proper playback
        const sortedEvents = [...processedEvents].sort((a, b) => {
          // Ensure FullSnapshot (type 2) is always first
          if (a.type === 2 && b.type !== 2) return -1;
          if (a.type !== 2 && b.type === 2) return 1;

          // Then sort by timestamp
          return a.timestamp - b.timestamp;
        });

        // console.log("Sorted events sample:", sortedEvents.slice(0, 5));
        // console.log("First event timestamp:", sortedEvents[0]?.timestamp);
        // console.log(
        //   "Last event timestamp:",
        //   sortedEvents[sortedEvents.length - 1]?.timestamp
        // );
        // console.log("Event types distribution:", {
        //   type2: sortedEvents.filter((e) => e.type === 2).length,
        //   type3: sortedEvents.filter((e) => e.type === 3).length,
        //   type4: sortedEvents.filter((e) => e.type === 4).length,
        // });

        setEvents(sortedEvents);
        // console.log(
        //   `Loaded ${sortedEvents.length} events from ${sortedChunks.length} chunks`
        // );
      } else {
        throw new Error("No recording chunks found in report");
      }

      setLoading(false);
    } catch (err) {
      console.error("Error fetching recording:", err);
      setError(err.message);
      setLoading(false);
    }
  };

  const processFullSnapshotEvent = (event) => {
    try {
      if (!event.data || !event.data.node) return event;

      const processedEvent = JSON.parse(JSON.stringify(event));

      const findAllNodes = (node, type = "") => {
        if (!node) return [];
        const results = [];
        if (!type || node.tagName === type) {
          results.push(node);
        }
        if (node.childNodes && Array.isArray(node.childNodes)) {
          node.childNodes.forEach((child) => {
            results.push(...findAllNodes(child, type));
          });
        }
        return results;
      };

      const allLinkNodes = findAllNodes(processedEvent.data.node, "link");
      allLinkNodes.forEach((linkNode) => {
        const attributes = linkNode.attributes || {};
        if (attributes.rel === "stylesheet" && attributes.href) {
          const isExternal =
            attributes.href.startsWith("http") &&
            !attributes.href.includes(window.location.host);
          if (isExternal) {
            const proxyUrl = `${API_URL}/reports/cors-proxy?url=${encodeURIComponent(
              attributes.href
            )}`;
            linkNode.attributes.href = proxyUrl;
          }
        }
      });

      return processedEvent;
    } catch (err) {
      console.error("Error processing FullSnapshot event:", err);
      return event;
    }
  };

  useEffect(() => {
    if (events.length > 0 && playerContainerRef.current) {
      initializePlayer(events);
    }
  }, [events]);

  // Handle window resize for responsiveness with debounce
  useEffect(() => {
    let resizeTimeout: NodeJS.Timeout;

    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        if (events.length > 0 && playerContainerRef.current) {
          initializePlayer(events);
        }
      }, 300); // Debounce for 300ms
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(resizeTimeout);
    };
  }, [events]);

  // Handle sidebar width changes
  useEffect(() => {
    if (events.length > 0 && playerContainerRef.current && containerWidth !== undefined) {
      const resizeTimeout = setTimeout(() => {
        initializePlayer(events);
      }, 100); // Small delay to allow container to resize

      return () => clearTimeout(resizeTimeout);
    }
  }, [containerWidth, events]);

  const initializePlayer = async (eventData) => {
    if (!playerContainerRef.current || !eventData || eventData.length === 0) {
      console.log(
        "Player initialization skipped - missing container or events"
      );
      return;
    }

    try {
      // Clean up existing player
      if (playerInstanceRef.current) {
        try {
          playerInstanceRef.current.pause?.();
          playerInstanceRef.current.destroy?.();
        } catch (e) {
          console.warn("Error cleaning up player:", e);
        }
        playerInstanceRef.current = null;
      }

      if (timelineIntervalRef.current) {
        clearInterval(timelineIntervalRef.current);
        timelineIntervalRef.current = null;
      }

      playerContainerRef.current.innerHTML = "";

      // Extract dimensions
      let recordedWidth = 1920;
      let recordedHeight = 1080;
      const metaEvent = eventData.find((e) => e.type === 4);
      if (metaEvent && metaEvent.data) {
        recordedWidth = metaEvent.data.width || recordedWidth;
        recordedHeight = metaEvent.data.height || recordedHeight;
      }

      const containerWidth = playerContainerRef.current.offsetWidth || 800;
      // Responsive max height based on screen size
      const isMobile = window.innerWidth < 1024;
      const maxHeight = isMobile
        ? window.innerHeight - 200
        : window.innerHeight - 250;
      const scale = Math.min(
        1,
        containerWidth / recordedWidth,
        maxHeight / recordedHeight
      );
      const scaledWidth = recordedWidth * scale;
      const scaledHeight = recordedHeight * scale;

      const wrapper = document.createElement("div");
      wrapper.style.cssText = `
        width: ${scaledWidth}px;
        height: ${scaledHeight}px;
        border-radius: 8px;
        overflow: hidden;
        display: block;
        box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        margin: 0 auto;
        pointer-events: none;
      `;

      playerContainerRef.current.appendChild(wrapper);

      const playerContainer = document.createElement("div");
      playerContainer.style.cssText = `
        width: ${scaledWidth}px;
        height: ${scaledHeight}px;
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;
        overflow: hidden;
        pointer-events: none;
      `;
      wrapper.appendChild(playerContainer);

      const replayWrapper = document.createElement("div");
      replayWrapper.className = "replayer-wrapper";
      replayWrapper.id = "rrweb-player";
      replayWrapper.style.cssText = `
        width: ${scaledWidth}px;
        height: ${scaledHeight}px;
        position: relative;
      `;
      playerContainer.appendChild(replayWrapper);

      const { Replayer } = await import("rrweb");
      // console.log("rrweb imported successfully");

      setPlayerReady(false);

      playerInstanceRef.current = new Replayer(eventData, {
        root: replayWrapper,
        skipInactive: true,
        showWarning: false,
        showDebug: false,
        blockClass: "rr-block",
        speed: 1,
        width: recordedWidth,
        height: recordedHeight,
        loadTimeout: 1000,
        showController: false,
        mouseTail: {
          duration: 500,
          lineCap: "round",
          lineWidth: 3,
          strokeStyle: "#ef4444",
        },
        UNSAFE_replayCanvas: false,
        pauseAnimation: false,
      });

      // console.log("✅ Replayer instance created successfully");
      // console.log("Player methods available:", {
      //   play: typeof playerInstanceRef.current.play,
      //   pause: typeof playerInstanceRef.current.pause,
      //   destroy: typeof playerInstanceRef.current.destroy,
      // });

      // Calculate total duration
      const firstTimestamp = eventData[0].timestamp;
      const lastTimestamp = eventData[eventData.length - 1].timestamp;
      const duration = lastTimestamp - firstTimestamp;

      // console.log("Timeline info:", {
      //   firstTimestamp,
      //   lastTimestamp,
      //   duration,
      //   durationSeconds: duration / 1000,
      //   eventCount: eventData.length,
      // });

      // Validate duration
      if (duration <= 0) {
        console.error(
          "❌ Invalid duration! All events have the same timestamp or are out of order"
        );
        throw new Error(
          "Invalid recording: duration is " +
            duration +
            "ms. Events may not be properly timestamped."
        );
      }

      if (duration < 100) {
        console.warn(
          "⚠️ Very short duration:",
          duration,
          "ms - this might be a recording issue"
        );
      }

      setTotalDuration(duration);
      setCurrentTime(0);

      setTimeout(() => {
        const iframe = replayWrapper.querySelector("iframe");
        if (iframe) {
          // console.log("✅ iframe found and styled");
          iframe.style.cssText = `
            width: ${recordedWidth}px !important;
            height: ${recordedHeight}px !important;
            transform: scale(${scale});
            transform-origin: top left;
            position: absolute;
            top: 0;
            left: 0;
            display: block !important;
            border: none;
          `;
        } else {
          console.warn("⚠️ iframe not found in replay wrapper");
        }

        // console.log("✅ Player ready, setting state");
        setPlayerReady(true);
      }, 100);
    } catch (err) {
      console.error("Error in initializePlayer:", err);
      setError("Failed to initialize player: " + err.message);

      if (playerContainerRef.current) {
        playerContainerRef.current.innerHTML = `
          <div style="padding: 20px; background: #fee; border: 1px solid #fcc; border-radius: 8px; color: #c00;">
            <h3>Player Initialization Error</h3>
            <p>${err.message}</p>
          </div>
        `;
      }
    }
  };

  const startMonitoring = () => {
    // console.log("=== startMonitoring called ===");
    if (timelineIntervalRef.current) {
      // console.log("Clearing existing interval");
      clearInterval(timelineIntervalRef.current);
    }

    const startTime = Date.now();
    const startPosition = currentTimeRef.current;
    // console.log("Starting monitoring from position:", startPosition);
    // console.log("isPlayingRef.current:", isPlayingRef.current);
    // console.log("totalDuration:", totalDuration);

    let tickCount = 0;
    timelineIntervalRef.current = setInterval(() => {
      tickCount++;
      if (tickCount <= 5) {
        console.log(
          `Tick ${tickCount}: isPlayingRef.current = ${isPlayingRef.current}`
        );
      }

      // Use ref instead of state
      if (isPlayingRef.current) {
        const elapsed = Date.now() - startTime;
        const newTime = Math.min(startPosition + elapsed, totalDuration);

        if (tickCount <= 5) {
          console.log(`  elapsed: ${elapsed}ms, newTime: ${newTime}ms`);
        }

        currentTimeRef.current = newTime;
        setCurrentTime(newTime);

        if (newTime >= totalDuration) {
          // console.log("Reached end of recording");
          stopMonitoring();
          isPlayingRef.current = false;
          setIsPlaying(false);
          try {
            playerInstanceRef.current?.pause();
          } catch (e) {
            console.warn("Error pausing player:", e);
          }
        }
      } else {
        if (tickCount === 1) {
          // console.log("⚠️ Interval running but isPlayingRef.current is FALSE!");
        }
      }
    }, 50);
    console.log(
      "✅ Monitoring interval started with ID:",
      timelineIntervalRef.current
    );
  };

  const stopMonitoring = () => {
    // console.log("=== stopMonitoring called ===");
    isPlayingRef.current = false;
    if (timelineIntervalRef.current) {
      clearInterval(timelineIntervalRef.current);
      timelineIntervalRef.current = null;
      // console.log("✅ Monitoring stopped");
    }
  };

  const togglePlay = useCallback(() => {
    if (!playerInstanceRef.current || !playerReady) {
      return;
    }

    try {
      const firstTimestamp = events[0]?.timestamp || 0;
      // console.log("firstTimestamp:", firstTimestamp);

      if (!isPlayingRef.current) {
        // console.log("▶️ Attempting to PLAY");

        // If at the end, restart from beginning
        if (currentTimeRef.current >= totalDuration) {
          // console.log("At end, restarting from 0");
          currentTimeRef.current = 0;
          setCurrentTime(0);
          console.log(
            "Calling playerInstance.play() with no args to start from beginning"
          );
          const result = playerInstanceRef.current.play();
          // console.log("Play result:", result);
        } else if (currentTimeRef.current === 0) {
          // Starting from beginning
          // console.log("Starting from beginning - calling play() with no args");
          const result = playerInstanceRef.current.play();
          // console.log("Play result:", result);
        } else {
          // Resume from specific time - use relative time
          const relativeTime = currentTimeRef.current;
          console.log(`Resuming from relative time: ${relativeTime}ms`);
          // console.log("First trying play() then using goto()");

          // Try using goto to seek, then play
          if (typeof playerInstanceRef.current.goto === "function") {
            // console.log("Player has goto method, using it");
            playerInstanceRef.current.goto(relativeTime);
            const result = playerInstanceRef.current.play();
            // console.log("Goto + Play result:", result);
          } else {
            // console.log("No goto method, trying play with relative time");
            const result = playerInstanceRef.current.play(relativeTime);
            // console.log("Play result:", result);
          }
        }

        // console.log("Setting isPlayingRef.current = true");
        isPlayingRef.current = true;
        // console.log("Calling setIsPlaying(true)");
        setIsPlaying(true);
        // console.log("Calling startMonitoring()");
        startMonitoring();
        // console.log("✅ Play command sent, monitoring started");
        // console.log("After play - isPlayingRef.current:", isPlayingRef.current);
      } else {
        // console.log("⏸️ Attempting to PAUSE");
        playerInstanceRef.current.pause();
        isPlayingRef.current = false;
        setIsPlaying(false);
        stopMonitoring();
        // console.log("✅ Pause command sent, monitoring stopped");
      }
    } catch (e) {
      console.error("❌ Error in togglePlay:", e);
      console.error("Error stack:", e.stack);
    }
  }, [playerReady, events, totalDuration]);

  const handleTimelineClick = (e) => {
    if (
      !playerInstanceRef.current ||
      !playerReady ||
      !timelineRef.current ||
      isDragging
    ) {
      return;
    }

    const rect = timelineRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, x / rect.width));
    const newTime = percentage * totalDuration;

    const wasPlaying = isPlayingRef.current;
    const firstTimestamp = events[0]?.timestamp || 0;

    if (wasPlaying) {
      stopMonitoring();
    }

    currentTimeRef.current = newTime;
    setCurrentTime(newTime);

    try {
      playerInstanceRef.current.pause();

      setTimeout(() => {
        playerInstanceRef.current.play(firstTimestamp + newTime);

        setTimeout(() => {
          if (wasPlaying) {
            isPlayingRef.current = true;
            setIsPlaying(true);
            startMonitoring();
          } else {
            playerInstanceRef.current.pause();
            isPlayingRef.current = false;
            setIsPlaying(false);
          }
        }, 50);
      }, 50);
    } catch (error) {
      console.warn("Error seeking:", error);
    }
  };

  const handleTimelineMouseDown = (e) => {
    setIsDragging(true);
    dragStartTimeRef.current = currentTimeRef.current;
    handleTimelineClick(e);
  };

  const handleTimelineMouseMove = (e) => {
    if (!isDragging || !timelineRef.current) return;

    const rect = timelineRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, x / rect.width));
    const newTime = percentage * totalDuration;

    currentTimeRef.current = newTime;
    setCurrentTime(newTime);
  };

  const handleTimelineMouseUp = () => {
    if (!isDragging) return;

    const wasPlaying = isPlayingRef.current;
    const prevTime = dragStartTimeRef.current || 0;
    const isSeekingBackward = currentTimeRef.current < prevTime;
    const firstTimestamp = events[0]?.timestamp || 0;

    if (isPlayingRef.current) {
      stopMonitoring();
    }

    try {
      playerInstanceRef.current.pause();

      if (isSeekingBackward) {
        setTimeout(() => {
          playerInstanceRef.current.play(firstTimestamp);
          setTimeout(() => {
            playerInstanceRef.current.pause();
            if (currentTimeRef.current > 0) {
              setTimeout(() => {
                playerInstanceRef.current.play(
                  firstTimestamp + currentTimeRef.current
                );
                setTimeout(() => {
                  if (!wasPlaying) {
                    playerInstanceRef.current.pause();
                    isPlayingRef.current = false;
                    setIsPlaying(false);
                  } else {
                    isPlayingRef.current = true;
                    setIsPlaying(true);
                    startMonitoring();
                  }
                }, 50);
              }, 50);
            }
          }, 50);
        }, 50);
      } else {
        setTimeout(() => {
          if (wasPlaying) {
            playerInstanceRef.current.play(
              firstTimestamp + currentTimeRef.current
            );
            isPlayingRef.current = true;
            setIsPlaying(true);
            startMonitoring();
          } else {
            playerInstanceRef.current.play(
              firstTimestamp + currentTimeRef.current
            );
            setTimeout(() => {
              playerInstanceRef.current.pause();
              isPlayingRef.current = false;
              setIsPlaying(false);
            }, 50);
          }
        }, 100);
      }
    } catch (error) {
      console.warn("Drag seek error:", error);
    }

    setIsDragging(false);
    dragStartTimeRef.current = null;
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleTimelineMouseMove);
      document.addEventListener("mouseup", handleTimelineMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleTimelineMouseMove);
        document.removeEventListener("mouseup", handleTimelineMouseUp);
      };
    }
  }, [isDragging, currentTime, isPlaying, totalDuration]);

  const formatTime = (ms) => {
    const seconds = Math.floor(ms / 1000);
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen((prev) => !prev);
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement && isFullscreen) {
        setIsFullscreen(false);
      }
    };

    const fullscreenContainer = fullscreenContainerRef.current;

    if (isFullscreen && fullscreenContainer) {
      document.body.style.overflow = "hidden";

      fullscreenContainer.requestFullscreen?.().catch((err) => {
        console.error("Error entering fullscreen:", err);
      });

      document.addEventListener("fullscreenchange", handleFullscreenChange);
    } else {
      document.body.style.overflow = "";

      if (document.fullscreenElement) {
        document.exitFullscreen().catch((err) => {
          console.error("Error exiting fullscreen:", err);
        });
      }
    }

    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, [isFullscreen]);

  // Handle spacebar for play/pause
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Don't trigger if user is typing in an input or editable element
      if (
        e.target.tagName === "INPUT" ||
        e.target.tagName === "TEXTAREA" ||
        e.target.isContentEditable ||
        e.target.getAttribute("contenteditable") === "true" ||
        e.target.closest("input") ||
        e.target.closest("textarea") ||
        e.target.closest('[contenteditable="true"]')
      ) {
        return;
      }

      if (e.code === "Space" && playerReady) {
        e.preventDefault();
        togglePlay();
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, [playerReady, togglePlay]);

  useEffect(() => {
    return () => {
      if (timelineIntervalRef.current) {
        clearInterval(timelineIntervalRef.current);
      }
      if (playerInstanceRef.current) {
        try {
          playerInstanceRef.current.pause?.();
          playerInstanceRef.current.destroy?.();
        } catch (e) {
          console.warn("Error destroying player on unmount:", e);
        }
      }
    };
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center w-full h-[400px]">
        <img
          src={VideoLoader}
          className="w-[56px] h-[56px] animate-spin"
          alt="Loading..."
        />
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          padding: "20px",
          background: "#fee",
          border: "1px solid #fcc",
          borderRadius: "8px",
          color: "#c00",
        }}
      >
        <h3 style={{ marginTop: 0 }}>Error Loading Replay</h3>
        <p>{error}</p>
        <button
          onClick={fetchRecording}
          style={{
            padding: "8px 16px",
            background: "#fff",
            color: "#c00",
            border: "1px solid #c00",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  const progress = totalDuration > 0 ? (currentTime / totalDuration) * 100 : 0;

  return (
    <div
      ref={fullscreenContainerRef}
      className="flex flex-col gap-4 w-full"
      style={isFullscreen ? {
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        backgroundColor: "black",
        padding: "20px",
        justifyContent: "center",
      } : {}}
    >
      <div
        className="w-full flex items-center justify-center cursor-pointer select-none"
        style={{ position: "relative", zIndex: 1, flex: isFullscreen ? 1 : "initial" }}
        onClick={(e) => {
          if (playerReady) {
            e.stopPropagation();
            togglePlay();
          }
        }}
        onDoubleClick={(e) => {
          if (playerReady) {
            e.stopPropagation();
            e.preventDefault();
            toggleFullscreen();
          }
        }}
      >
        <div ref={playerContainerRef} style={{ width: "100%", position: "relative" }} />

        {!playerReady && !error && (
          <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/10 z-[3]">
            <img
              src={VideoLoader}
              className="w-[56px] h-[56px] animate-spin"
              alt="Loading..."
            />
          </div>
        )}
      </div>

      {/* Controls */}
      <div
        className="flex items-center gap-2 shrink-0 min-w-0 w-full"
        style={{
          position: "relative",
          zIndex: 10,
        }}
      >
        <button
          onClick={togglePlay}
          disabled={!playerReady}
          className="bg-white/[0.08] backdrop-blur-md flex items-center justify-center w-[32px] h-[32px] rounded-[10px] hover:bg-white/[0.16] transition-all cursor-pointer shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isPlaying ? (
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"
                fill="white"
              />
            </svg>
          ) : (
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path d="M8 5v14l11-7z" fill="white" />
            </svg>
          )}
        </button>

        {/* Timeline */}
        <div className="flex-1 relative flex items-center h-[32px]">
          <div
            ref={timelineRef}
            data-timeline
            className="group w-full h-[32px] rounded-[10px] relative cursor-pointer backdrop-blur-md bg-white/[0.08] overflow-hidden"
            onMouseDown={handleTimelineMouseDown}
          >
            <div
              className="absolute top-0 left-0 h-full bg-white pointer-events-none"
              style={{
                width: `${progress}%`,
                transition: isDragging ? "none" : "width 0.1s linear",
              }}
            />
          </div>
        </div>

        {/* Time display */}
        <div className="backdrop-blur-md text-white/80 text-[14px] px-[10px] leading-[20px] bg-white/[0.08] transition-all rounded-[10px] font-medium h-[32px] flex items-center shrink-0 whitespace-nowrap overflow-hidden">
          <span className="truncate">
            {formatTime(currentTime)} / {formatTime(totalDuration)}
          </span>
        </div>

        {/* Fullscreen button */}
        <button
          onClick={toggleFullscreen}
          className="bg-white/[0.08] backdrop-blur-md flex items-center justify-center w-[32px] h-[32px] rounded-[10px] hover:bg-white/[0.16] transition-all cursor-pointer shrink-0"
        >
          <img src={Resize} alt="Fullscreen" className="w-[20px] h-[20px]" />
        </button>
      </div>
    </div>
  );
};

export default DOMReplayViewer;
